import React, { useState } from 'react';
import { Form } from '../../../services/formService';
import { Task } from '../../../types/Task';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { Alert, Button, ButtonGroup, Form as UiForm, Spinner } from '@/components/ui';
import HeVendorApprovalEmailModal from '../../hired-equipment/components/HeVendorApprovalEmailModal';
import { getHiredEquipmentInternalComment } from '../../hired-equipment/utils/internalComment';
import {
  canApproveSubmissionForForm,
  canDeclineSubmissionForForm,
  canSendForRevisions,
  canWithdrawApprovalForForm,
  canWithdrawDeclineForForm,
  canWithdrawRevisionsForForm
} from '../utils/submissionPermissions';
import '../styles/FormList.css';
import { getFormEmployeeDisplayName } from '../utils/formDisplayName';
import { buildHiredEquipmentPdfFilename } from '../utils/hePdfFilename';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { DAILY_LOG_CARD_LAYOUT_QUERY } from '../utils/dailyLogLayout';
import { formatDate } from '@/utils/displayDateTime';
import { confirmAction } from '@/components/ui';

interface FormActionBarProps {
  form: Form;
  viewMode: 'summary' | 'detailed';
  onViewModeChange: (mode: 'summary' | 'detailed') => void;
  /** HE-F21: `emailVendorPdf` is the approver's answer to the vendor-PDF prompt. */
  onApprove?: (
    formId: string,
    options?: { emailVendorPdf?: boolean; vendorEmail?: { to: string; subject: string; bodyText: string } },
  ) => Promise<void>;
  onReject?: (formId: string, comment: string) => Promise<void>;
  onDecline?: (formId: string, comment: string) => Promise<void>;
  onWithdrawApproval?: (formId: string) => Promise<void>;
  onWithdrawRevisions?: (formId: string) => Promise<void>;
  onWithdrawDecline?: (formId: string) => Promise<void>;
  hasInvalidActivity?: boolean;
  hasIneligibleTask?: boolean;
  /** Persisted DRM labour allocations conflict with Task Duration (Hours) zero on a line */
  hasTaskDurationAllocationConflict?: boolean;
  hasZeroHours?: boolean;
  availableTasks?: Task[];
  isProcessing?: boolean;
  processingAction?: string | null;
  isFading?: boolean;
  fadingAction?: string | null;
  /** Pending or approved daily log: edit DRM labour OT buckets (form-level) when `FormCard` enables it. */
  showEditLabourOtBuckets?: boolean;
  onEditLabourOtBuckets?: () => void | Promise<void>;
  /** True while ensuring allocation rows exist before opening the bucket editor. */
  editLabourOtBucketsBusy?: boolean;
  /**
   * Daily logs (W-iP5): on tablet and phone keep the bar pinned to the top of the scroll area so
   * Approve / Send for Revisions / Decline stay in reach while scrolling the entries. Hired
   * equipment leaves this off and behaves as before.
   */
  stickyOnCompact?: boolean;
}
  
const FormActionBar: React.FC<FormActionBarProps> = ({
  form,
  viewMode,
  onViewModeChange,
  onApprove,
  onReject,
  onDecline,
  onWithdrawApproval,
  onWithdrawRevisions,
  onWithdrawDecline,
  hasInvalidActivity = false,
  hasIneligibleTask = false,
  hasTaskDurationAllocationConflict = false,
  hasZeroHours = false,
  availableTasks,
  isProcessing = false,
  processingAction = null,
  isFading = false,
  fadingAction = null,
  showEditLabourOtBuckets = false,
  onEditLabourOtBuckets,
  editLabourOtBucketsBusy = false,
  stickyOnCompact = false
}) => {
  const { currentUser } = useAuth();
  const compactLayout = useMediaQuery(DAILY_LOG_CARD_LAYOUT_QUERY);
  const pinned = stickyOnCompact && compactLayout;
  const { hasPermission } = usePermissions();
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [revisionComment, setRevisionComment] = useState('');
  const [declineComment, setDeclineComment] = useState('');
  // V2-019 / FORM-01: the buttons stay enabled; a missing comment shows under the field.
  const [revisionCommentError, setRevisionCommentError] = useState(false);
  const [declineCommentError, setDeclineCommentError] = useState(false);
  const [showVendorEmailModal, setShowVendorEmailModal] = useState(false);
  const [vendorChoicePending, setVendorChoicePending] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  // Shown inline in the action bar (STATE-06), not in a browser alert.
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Define processing states at component level for use in modals
  const isApproveProcessing = isProcessing && processingAction === 'approve';
  const isRejectProcessing = isProcessing && processingAction === 'reject';
  const isDeclineProcessing = isProcessing && processingAction === 'decline';
  const isApproveFading = isFading && fadingAction === 'approve';
  const isRejectFading = isFading && fadingAction === 'reject';
  const isDeclineFading = isFading && fadingAction === 'decline';
  const isWithdrawApprovalProcessing = isProcessing && processingAction === 'withdrawApproval';
  const isWithdrawApprovalFading = isFading && fadingAction === 'withdrawApproval';
  const isWithdrawRevisionsProcessing = isProcessing && processingAction === 'withdrawRevisions';
  const isWithdrawRevisionsFading = isFading && fadingAction === 'withdrawRevisions';
  const isWithdrawDeclineProcessing = isProcessing && processingAction === 'withdrawDecline';
  const isWithdrawDeclineFading = isFading && fadingAction === 'withdrawDecline';
  const anyProcessing = isProcessing || isFading;

  // Determine if it's a Hired Equipment form or Daily Log
  const isHiredEquipmentForm = form.fields.some(field =>
    field.path.includes('EquipmentTimeWorkedJ') ||
    field.field === 'AmountBilled' ||
    field.path.includes('SubcontractorNameCom')
  );

  const heOverallComment = getHiredEquipmentInternalComment(form.fields);

  // H1: nobody approves, sends back or declines their own hired equipment submission. The server
  // enforces this too; hiding the buttons avoids offering an action that would be refused.
  const isOwnHeSubmission = Boolean(
    isHiredEquipmentForm && currentUser && form.statusRequestedBy?.id != null && form.statusRequestedBy.id === currentUser.id
  );

  const canApproveSubmission = canApproveSubmissionForForm(hasPermission, form) && !isOwnHeSubmission;
  const canDeclineSubmission = canDeclineSubmissionForForm(hasPermission, form) && !isOwnHeSubmission;
  const canSendForRevisionsAction = canSendForRevisions(hasPermission, form) && !isOwnHeSubmission;
  const canWithdrawApproval = canWithdrawApprovalForForm(hasPermission, form);
  const canWithdrawRevisions = canWithdrawRevisionsForForm(hasPermission, form);
  const canWithdrawDecline = canWithdrawDeclineForForm(hasPermission, form);
  const showFormActionBar =
    canApproveSubmission ||
    canDeclineSubmission ||
    canSendForRevisionsAction ||
    canWithdrawApproval ||
    canWithdrawRevisions ||
    canWithdrawDecline ||
    (Boolean(showEditLabourOtBuckets) && Boolean(onEditLabourOtBuckets));

  // Get employee name to check if user is trying to approve their own timecard
  const employeeName = form.fields.find(f => f.field === 'EmployeeName')?.value || '';
  const currentUserFullName = currentUser ? `${currentUser.name} ${currentUser.lastName}` : '';
  const isOwnTimecard = employeeName === currentUserFullName;

  // Why Approve is blocked, shown inline next to the button (DLG-06 / STATE-06) rather than in a
  // hover-only tooltip or a browser alert. First matching reason wins.
  const approveBlockedReason: string | null = hasInvalidActivity
    ? "Can't approve: change the tasks with activity number 999300 first."
    : hasIneligibleTask
      ? "Can't approve: a task on this submission is closed, finalized, cancelled or proposed and can't receive effort. Reassign the task first."
      : hasTaskDurationAllocationConflict
        ? "Can't approve: Task Duration (Hours) is 0 on a line that still has labour overtime allocation hours. Fix Task Duration or the OT allocations first."
        : isOwnTimecard
          ? "You can't approve your own timecard."
          : null;

  const handleApprove = async () => {
    if (approveBlockedReason) return;

    if (hasZeroHours) {
      const confirmed = (await confirmAction({ title: 'Approve Timecard With No Hours?', body: 'This timecard has no hours entered.', confirmLabel: 'Approve' }));
      if (!confirmed) return;
    }

    // HE-F21. Ask before approving, not after: the answer travels with the approval so the
    // send and the history entry happen in the same server call. Keep the modal open until
    // that call finishes so approval is not treated as done while the prompt is still up.
    if (isHiredEquipmentForm) {
      setShowVendorEmailModal(true);
      return;
    }

    await onApprove?.(form.id);
  };

  const vendorEmailBusy = isApproveProcessing || isApproveFading || vendorChoicePending;

  const handleVendorEmailChoice = async (
    emailVendorPdf: boolean,
    vendorEmail?: { to: string; subject: string; bodyText: string },
  ) => {
    if (vendorEmailBusy) return;
    setVendorChoicePending(true);
    try {
      await onApprove?.(form.id, { emailVendorPdf, vendorEmail });
      setShowVendorEmailModal(false);
    } finally {
      setVendorChoicePending(false);
    }
  };

  const handleRevisionSubmit = async () => {
    if (!revisionComment.trim()) {
      setRevisionCommentError(true);
      document.getElementById('revisionComment')?.focus();
      return;
    }
    await onReject?.(form.id, revisionComment);
    setShowRevisionModal(false);
    setRevisionComment('');
  };

  const handleDeclineSubmit = async () => {
    if (!declineComment.trim()) {
      setDeclineCommentError(true);
      document.getElementById('declineComment')?.focus();
      return;
    }
    await onDecline?.(form.id, declineComment);
    setShowDeclineModal(false);
    setDeclineComment('');
  };

  const handleWithdrawApproval = async () => {
    await onWithdrawApproval?.(form.id);
  };

  const handleWithdrawRevisions = async () => {
    await onWithdrawRevisions?.(form.id);
  };

  const handleWithdrawDecline = async () => {
    await onWithdrawDecline?.(form.id);
  };

  const findFormDate = (fields: Form['fields']): string => {
    // PRIORITY 1: Look for any ActualStartTime field regardless of path
    const actualStartTimeFields = fields.filter(f => 
      f.field === 'ActualStartTime' && 
      f.value?.trim()
    );
    
    if (actualStartTimeFields.length > 0) {
      for (const field of actualStartTimeFields) {
        try {
          const date = new Date(field.value);
          if (!isNaN(date.getTime())) {
            return formatDate(date);
          }
        } catch (error) {
          console.log('Error parsing ActualStartTime:', field.value);
        }
      }
    }
    
    // PRIORITY 2: Look for other common date/time fields as fallback
    const otherDateFields = [
      'Start Date/Time',
      'StartDateTime', 
      'ActualEndTime',
      'End Date/Time',
      'EndDateTime',
      'PatrolDate',
      'ShiftDate',
      'WorkDate',
      'FormDate',
      'Date'
    ];
    
    for (const fieldName of otherDateFields) {
      const dateFields = fields.filter(f => f.field === fieldName && f.value?.trim());
      if (dateFields.length > 0) {
        const field = dateFields[0];
        try {
          const date = new Date(field.value);
          if (!isNaN(date.getTime())) {
            return formatDate(date);
          }
        } catch (error) {
          console.log('Error parsing date from', fieldName, ':', field.value);
        }
      }
    }
    
    // PRIORITY 3: Last resort - any field with time/date in name
    const timeFields = fields.filter(f =>
      (f.field?.toLowerCase().includes('time') ||
        f.field?.toLowerCase().includes('date') ||
        f.question?.toLowerCase().includes('time') ||
        f.question?.toLowerCase().includes('date')) &&
      f.value?.trim()
    );
    
    if (timeFields.length > 0) {
      for (const field of timeFields) {
        try {
          const date = new Date(field.value);
          if (!isNaN(date.getTime())) {
            return formatDate(date);
          }
        } catch (error) {
          console.log('Error parsing date from', field.field, ':', field.value);
        }
      }
    }
    
    return 'Unknown Date';
  };

  const handlePrintPDF = async () => {
    if (isGeneratingPDF) return; // Prevent multiple clicks
    
    setIsGeneratingPDF(true);
    setPdfError(null);
    try {
      const employeeName = getFormEmployeeDisplayName(form);
      const formDate = findFormDate(form.fields);
      
      const endpoint = isHiredEquipmentForm 
        ? '/api/pdf/generate-hired-equipment-pdf' 
        : '/api/pdf/generate-pdf';
      const filename = isHiredEquipmentForm
        ? buildHiredEquipmentPdfFilename(form)
        : `Daily_Log_${employeeName}_${formDate}_Form_${form.id}.pdf`;
      
      // Call the appropriate backend PDF generation endpoint
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form,
          availableTasks
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get the PDF blob from the response
      const pdfBlob = await response.blob();
      
      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setPdfError("Couldn't generate the PDF. Try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const getActionButtons = () => {
    const isPending = form.status === 'pending' || form.status === 'revisions completed';
    const isApproved = form.status === 'approved';
    const needsRevisions = form.status === 'need revisions';
    const isDeclined = form.status === 'declined';
    
    if (isDeclined) {
      if (!canWithdrawDecline) {
        return null;
      }
      return (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <Button
            onClick={handleWithdrawDecline}
            variant="warning"
            size="sm"
            className={isWithdrawDeclineFading ? 'form-action-fade-out' : ''}
            disabled={anyProcessing}
            title="Withdraw decline and return submission to pending status"
          >
            {isWithdrawDeclineProcessing ? (
              <>
                <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                Processing...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-counterclockwise tw-mr-1"></i>
                Withdraw Decline
              </>
            )}
          </Button>
        </div>
      );
    }
    if (isPending) {
      return (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <span
            className="tw-inline-block"
            title={
              hasInvalidActivity ? 'Cannot approve: Invalid activity (999300)' :
              hasIneligibleTask ? 'Cannot approve: Task is closed/cancelled/finalized/proposed - must reassign' :
              hasTaskDurationAllocationConflict ? 'Cannot approve: Task Duration is 0 but DRM labour allocations have hours – correct before approval' :
              isOwnTimecard ? 'You are unable to approve your own timecards' :
              'Approve submission'
            }
          >
            <Button
              variant="success"
              size="sm"
              className={isApproveFading ? 'form-action-fade-out' : ''}
              onClick={handleApprove}
              disabled={
                !canApproveSubmission ||
                hasInvalidActivity ||
                hasIneligibleTask ||
                hasTaskDurationAllocationConflict ||
                isOwnTimecard ||
                anyProcessing
              }
            >
            {isApproveProcessing ? (
              <>
                <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                Approving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle tw-mr-1"></i>
                Approve
              </>
            )}
          </Button>
          </span>
          {canSendForRevisionsAction && (
            <Button
              variant="warning"
              size="sm"
              className={isRejectFading ? 'form-action-fade-out' : ''}
              onClick={() => setShowRevisionModal(true)}
              disabled={anyProcessing}
              style={{ backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#000' }}
              title="Send for Revisions"
            >
              {isRejectProcessing ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise tw-mr-1"></i>
                  Send for Revisions
                </>
              )}
            </Button>
          )}
          {canDeclineSubmission && (
          <Button
            variant="danger"
            size="sm"
            className={isDeclineFading ? 'form-action-fade-out' : ''}
            onClick={() => setShowDeclineModal(true)}
            disabled={anyProcessing}
            title="Decline submission"
          >
            {isDeclineProcessing ? (
              <>
                <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                Processing...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle tw-mr-1"></i>
                Decline
              </>
            )}
          </Button>
          )}
          {showEditLabourOtBuckets && onEditLabourOtBuckets && (
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={() => void onEditLabourOtBuckets()}
              disabled={isGeneratingPDF || anyProcessing || editLabourOtBucketsBusy}
              title="Edit straight / OT 1.5 / OT 2 totals for this submission"
            >
              {editLabourOtBucketsBusy ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                  Preparing…
                </>
              ) : (
                <>
                  <i className="bi bi-sliders tw-mr-1"></i>
                  Edit OT Buckets
                </>
              )}
            </Button>
          )}
        </div>
      );
    }

    if (isApproved) {
      return (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {/* Print PDF Button - Available for both Daily Log and Hired Equipment forms */}
          <Button
            onClick={handlePrintPDF}
            variant="primary"
            size="sm"
            disabled={isGeneratingPDF || anyProcessing}
            title={isGeneratingPDF ? 'Generating PDF...' : 'Print form as PDF'}
          >
            {isGeneratingPDF ? (
              <>
                <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                Generating...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-pdf tw-mr-1"></i>
                Print PDF
              </>
            )}
          </Button>

          {showEditLabourOtBuckets && onEditLabourOtBuckets && (
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={() => void onEditLabourOtBuckets()}
              disabled={isGeneratingPDF || anyProcessing || editLabourOtBucketsBusy}
              title="Edit straight / OT 1.5 / OT 2 totals for this submission"
            >
              {editLabourOtBucketsBusy ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                  Preparing…
                </>
              ) : (
                <>
                  <i className="bi bi-sliders tw-mr-1"></i>
                  Edit OT Buckets
                </>
              )}
            </Button>
          )}

          {/* Withdraw Approval Button - Available for all form types with proper permission */}
          {canWithdrawApproval && (
            <Button
              onClick={handleWithdrawApproval}
              variant="warning"
              size="sm"
              className={isWithdrawApprovalFading ? 'form-action-fade-out' : ''}
              disabled={anyProcessing}
              title="Withdraw approval and return form to pending status"
            >
              {isWithdrawApprovalProcessing ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-counterclockwise tw-mr-1"></i>
                  Withdraw Approval
                </>
              )}
            </Button>
          )}

          {/* Withdraw Approval Button - Disabled for users without permission */}
          {!canWithdrawApproval && (
            <Button
              disabled
              variant="outline-secondary"
              size="sm"
              title="Only Ops Managers can withdraw approvals"
            >
              <i className="bi bi-arrow-counterclockwise tw-mr-1"></i>
              Withdraw Approval
            </Button>
          )}
        </div>
      );
    }

    if (needsRevisions) {
      return (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {/* Withdraw Revisions Button */}
          {canWithdrawRevisions && (
            <Button
              onClick={handleWithdrawRevisions}
              variant="info"
              size="sm"
              className={isWithdrawRevisionsFading ? 'form-action-fade-out' : ''}
              disabled={anyProcessing}
              title="Withdraw revision request and return form to pending status"
            >
              {isWithdrawRevisionsProcessing ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-counterclockwise tw-mr-1"></i>
                  Withdraw Revision
                </>
              )}
            </Button>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div
        className={`form-actionbar tw-border-b tw-border-line tw-bg-surface-alt tw-p-3 tw-mb-3${
          pinned ? ' form-actionbar--pinned tw-sticky tw-top-0 tw-z-20 tw-shadow-sm' : ''
        }`}
        data-testid="form-action-bar"
        data-pinned={pinned ? 'true' : undefined}
      >
        <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-justify-between sm:tw-items-center">
          <ButtonGroup className="tw-w-full sm:tw-w-auto">
            <Button
              type="button"
              variant={viewMode === 'summary' ? 'primary' : 'outline-primary'}
              onClick={() => onViewModeChange('summary')}
              className="tw-grow sm:tw-grow-0"
            >
              <i className="bi bi-list-ul tw-mr-1"></i>
              Summary View
            </Button>
            <Button
              type="button"
              variant={viewMode === 'detailed' ? 'primary' : 'outline-primary'}
              onClick={() => onViewModeChange('detailed')}
              className="tw-grow sm:tw-grow-0"
            >
              <i className="bi bi-card-text tw-mr-1"></i>
              Detailed View
            </Button>
          </ButtonGroup>

          {showFormActionBar && getActionButtons()}
        </div>
        {showFormActionBar && canApproveSubmission && approveBlockedReason &&
          (form.status === 'pending' || form.status === 'revisions completed') && (
          <Alert variant="warning" role="alert" className="tw-mt-2 tw-mb-0 tw-py-2 tw-text-sm" data-testid="approve-blocked-reason">
            {approveBlockedReason}
          </Alert>
        )}
        {pdfError && (
          <Alert variant="danger" role="alert" dismissible onClose={() => setPdfError(null)} className="tw-mt-2 tw-mb-0 tw-py-2 tw-text-sm">
            {pdfError}
          </Alert>
        )}
      </div>


      <HeVendorApprovalEmailModal
        isOpen={showVendorEmailModal}
        formId={form.id}
        busy={vendorEmailBusy}
        onCancel={() => {
          if (vendorEmailBusy) return;
          setShowVendorEmailModal(false);
        }}
        onSkip={() => handleVendorEmailChoice(false)}
        onSend={(draft) => handleVendorEmailChoice(true, draft)}
      />

      {/* Revision Modal — Daily Log and Hired Equipment (comment required, same as decline) */}
      <ResponsiveModal
          isOpen={showRevisionModal}
          onClose={() => setShowRevisionModal(false)}
          title="Send for Revisions"
          footer={
            <div className="tw-flex tw-gap-2">
              <Button type="button" variant="secondary" onClick={() => setShowRevisionModal(false)} disabled={isProcessing || isFading}>
                Cancel
              </Button>
              <Button type="button" variant="warning" onClick={handleRevisionSubmit} disabled={isProcessing || isFading}>
                {isRejectProcessing ? (
                  <>
                    <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                    Processing...
                  </>
                ) : (
                  'Send for Revisions'
                )}
              </Button>
            </div>
          }
        >
          {/* M23: the submission's overall comment, so the reviewer sees it while sending back. */}
          {isHiredEquipmentForm && heOverallComment && (
            <div className="tw-mb-3 tw-rounded tw-border tw-border-line tw-bg-surface-alt tw-p-3">
              <p className="tw-mb-1 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">
                Submission comment
              </p>
              <p className="tw-mb-0 tw-whitespace-pre-wrap tw-text-sm tw-text-content">{heOverallComment}</p>
            </div>
          )}
          <div className="tw-mb-3">
            <UiForm.Label htmlFor="revisionComment">
              Comment <span className="tw-text-danger">*</span>
            </UiForm.Label>
            <UiForm.Control
              as="textarea"
              id="revisionComment"
              rows={6}
              value={revisionComment}
              onChange={(e) => {
                setRevisionComment(e.target.value);
                if (e.target.value.trim()) setRevisionCommentError(false);
              }}
              placeholder="Please provide a reason for this action..."
              required
              isInvalid={revisionCommentError}
              aria-describedby={revisionCommentError ? 'revisionCommentError' : undefined}
            />
            {revisionCommentError && (
              <UiForm.Control.Feedback type="invalid" id="revisionCommentError">
                Comment required
              </UiForm.Control.Feedback>
            )}
          </div>
      </ResponsiveModal>

      {/* Decline Modal */}
      <ResponsiveModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        title="Decline Submission"
        footer={
          <div className="tw-flex tw-gap-2">
            <Button type="button" variant="secondary" onClick={() => setShowDeclineModal(false)} disabled={isProcessing || isFading}>
              Cancel
            </Button>
            <Button type="button" variant="danger" onClick={handleDeclineSubmit} disabled={isProcessing || isFading}>
              {isDeclineProcessing ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" role="status" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                'Decline'
              )}
            </Button>
          </div>
        }
      >
        <div className="tw-mb-3">
          {/* Warning Message */}
          <Alert variant="warning" role="alert">
            <i className="bi bi-exclamation-triangle tw-mr-2"></i>
            <strong>Note:</strong> Declined submissions can be returned to pending by users with Withdraw Decline permission.
          </Alert>

          <UiForm.Label htmlFor="declineComment">
            Comment <span className="tw-text-danger">*</span>
          </UiForm.Label>
          <UiForm.Control
            as="textarea"
            id="declineComment"
            rows={6}
            value={declineComment}
            onChange={(e) => {
              setDeclineComment(e.target.value);
              if (e.target.value.trim()) setDeclineCommentError(false);
            }}
            placeholder="Please provide a reason for this action..."
            required
            isInvalid={declineCommentError}
            aria-describedby={declineCommentError ? 'declineCommentError' : undefined}
          />
          {declineCommentError && (
            <UiForm.Control.Feedback type="invalid" id="declineCommentError">
              Comment required
            </UiForm.Control.Feedback>
          )}
        </div>
      </ResponsiveModal>
    </>
  );
};

export default FormActionBar;